<template>
  <div class="max-w-3xl mx-auto space-y-3 py-3">
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <div class="mb-2">
        <h2 class="text-base font-semibold text-gray-800">Student ID Card</h2>
        <p class="text-xs text-gray-500">
          Branding for the printable ID card. The card itself is generated from the student's record —
          open a student and use the ID Card action.
        </p>
      </div>

      <Divider />

      <div class="space-y-2.5">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-0.5">Card Colour</label>
          <div class="flex items-center gap-2">
            <ColorPicker v-model="swatch" data-testid="id-card-colour-picker" />
            <InputText
              v-model="config.primary_color"
              placeholder="#1D4ED8"
              class="w-40 font-mono"
              data-testid="id-card-colour-hex"
            />
          </div>
          <small v-if="!colorIsValid" class="text-red-600 block mt-0.5">
            Enter a hex colour such as #1D4ED8.
          </small>
          <small v-else class="text-gray-500 block mt-0.5">
            Used for the header band, the student's name and the card border.
          </small>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 mb-0.5">Tagline (optional)</label>
          <InputText
            v-model="config.tagline"
            placeholder="e.g. Knowledge, Integrity, Service"
            class="w-full md:w-2/3"
            :maxlength="60"
            data-testid="id-card-tagline"
          />
          <small class="text-gray-500 block mt-0.5">Printed under the school name. Leave blank to hide it.</small>
        </div>

        <div data-testid="id-card-extra-fields">
          <label for="id-card-extra-fields" class="block text-xs font-medium text-surface-700 mb-0.5">
            Extra details (optional, up to {{ MAX_ID_CARD_EXTRA_FIELDS }})
          </label>
          <MultiSelect
            v-model="config.extra_fields"
            input-id="id-card-extra-fields"
            :options="documentFields"
            option-label="label"
            option-value="key"
            :selection-limit="MAX_ID_CARD_EXTRA_FIELDS"
            display="chip"
            filter
            placeholder="e.g. Blood Group"
            class="w-full md:w-2/3"
          />
          <small class="text-surface-500 block mt-0.5">
            Printed under Programme, Department and Level. Long values such as addresses are shortened to fit the card.
          </small>
        </div>

        <div>
          <span class="block text-xs font-medium text-gray-700 mb-1">Preview</span>
          <div class="rounded-lg overflow-hidden border border-gray-200 w-[257px]">
            <div class="px-2 py-1.5 text-white" :style="{ backgroundColor: previewColor }">
              <div class="text-[10px] font-bold leading-tight">{{ schoolName || 'Your School' }}</div>
              <div v-if="config.tagline" class="text-[7px] leading-tight">{{ config.tagline }}</div>
            </div>
            <div class="flex gap-2 p-2 bg-white">
              <div class="w-[38px] h-[46px] bg-gray-100 border" :style="{ borderColor: previewColor }"></div>
              <div class="flex-1">
                <div class="text-[10px] font-bold" :style="{ color: previewColor }">Student Name</div>
                <div class="text-[8px] text-gray-700">MAT/0000/00000</div>
                <div class="text-[7px] text-gray-500 mt-0.5">PROGRAMME · DEPARTMENT · LEVEL</div>
                <div v-for="label in extraFieldLabels" :key="label" class="text-[7px] text-surface-500">
                  {{ label.toUpperCase() }}: …
                </div>
              </div>
            </div>
            <div class="px-2 py-1 bg-gray-50 text-[7px] text-gray-600 border-t" :style="{ borderColor: previewColor }">
              Valid: {{ '2025/2026' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <Button label="Save" icon="pi pi-check" size="small" :disabled="!colorIsValid" :loading="saving" @click="save" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useToast } from 'primevue/usetoast';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { UPDATE_SCHOOL_SETTINGS } from '@/graphql';
import { useStudentDocumentFields } from '@/composables/queries/useStudentDocumentFields';
import { MAX_ID_CARD_EXTRA_FIELDS, pruneDocumentFields } from '@/utils/studentDocumentFields';

/**
 * ID-card branding (#374 phase 1) and extra student details (#574). There is
 * no dedicated mutation: the values live under `settings.id_card`, and `updateSchoolSettings` merges an
 * object key-by-key, so sending just this subtree leaves every other setting
 * alone.
 */
const DEFAULT_COLOR = '#1D4ED8';

const ID_CARD_SETTINGS = gql`
  query IdCardSettings {
    getSchool {
      id
      name
      settings
    }
  }
`;

const toast = useToast();

const config = ref<{ primary_color: string; tagline: string; extra_fields: string[] }>({
  primary_color: DEFAULT_COLOR,
  tagline: '',
  extra_fields: [],
});

// Extra student facts for the card (#574): built-in fields such as State of
// Origin, or the school's custom fields. The backend re-checks the keys.
const { fields: documentFields } = useStudentDocumentFields();
const extraFieldLabels = computed(() => config.value.extra_fields
  .map((key) => documentFields.value.find((field) => field.key === key)?.label)
  .filter((label): label is string => !!label));
const schoolName = ref('');

const { result, refetch } = useQuery(ID_CARD_SETTINGS, null, { fetchPolicy: 'network-only' });

watch(result, (val) => {
  const school = val?.getSchool;
  if (!school) return;
  schoolName.value = school.name ?? '';
  const stored = school.settings?.id_card ?? {};
  config.value = {
    primary_color: stored.primary_color ?? school.settings?.branding?.primary_color ?? DEFAULT_COLOR,
    tagline: stored.tagline ?? '',
    extra_fields: Array.isArray(stored.extra_fields) ? stored.extra_fields.filter((key: unknown) => typeof key === 'string') : [],
  };
}, { immediate: true });

const colorIsValid = computed(() => /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(config.value.primary_color));
const previewColor = computed(() => (colorIsValid.value ? config.value.primary_color : DEFAULT_COLOR));

// PrimeVue's ColorPicker models a bare hex with no leading '#'; the text field
// and everything downstream want the '#', so the two are bridged here rather
// than storing a second shape.
const swatch = computed({
  get: () => previewColor.value.replace('#', ''),
  set: (value: string) => {
    if (typeof value === 'string' && value) {
      config.value.primary_color = value.startsWith('#') ? value : `#${value}`;
    }
  },
});

const { mutate: updateSettings, loading: saving } = useMutation(UPDATE_SCHOOL_SETTINGS);

async function save() {
  if (!colorIsValid.value) return;
  try {
    await updateSettings({
      settings: {
        id_card: {
          primary_color: config.value.primary_color,
          tagline: config.value.tagline.trim() || null,
          extra_fields: pruneDocumentFields(config.value.extra_fields, documentFields.value).slice(0, MAX_ID_CARD_EXTRA_FIELDS),
        },
      },
    });
    await refetch();
    toast.add({ severity: 'success', summary: 'Saved', detail: 'ID card settings updated.', life: 3000 });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e instanceof Error ? e.message : 'Failed to save ID card settings.',
      life: 3000,
    });
  }
}
</script>
